import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useMetaTags } from '@/hooks/useMetaTags';
import { Loader2 } from 'lucide-react';
import { useSettings } from "@/hooks/useSettings";
import { optimizeCloudinaryUrl } from "@/lib/utils";
import { useAlbumNavigation } from "@/hooks/useAlbumNavigation";
import { useAlbumContext } from '@/contexts/AlbumProvider';


const Auth = () => {
  const { user, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const { getSetting, getBooleanSetting } = useSettings();
  const coverUrl = getSetting('image_cover');
  const optimizedCoverUrl = coverUrl && optimizeCloudinaryUrl({ originalUrl: coverUrl, width: 1900 })
  const { getAlbumPath, loading: albumLoading } = useAlbumNavigation();
  const { albumSlug } = useAlbumContext();

  useMetaTags({
    title: 'Iniciar Sesión',
    pathname: '/login'
  });

  useEffect(() => {
    // Redirect authenticated users - wait for album context to load
    if (user && !albumLoading) {
      // Check if there's a redirect parameter in the URL
      const searchParams = new URLSearchParams(location.search);
      const redirectTo = searchParams.get('redirect');

      if (redirectTo) {
        // Redirect to the original intended page
        navigate(decodeURIComponent(redirectTo), { replace: true });
      } else {
        // Default redirect to inicio
        const albumPath = getAlbumPath('/inicio');
        if (albumPath) {
          navigate(albumPath, { replace: true });
        }
      }
    }
  }, [user, navigate, albumLoading, getAlbumPath, location.search]);

  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);
      await signInWithGoogle();
    } catch (error: any) {
      console.error('Error signing in:', error);
      toast({
        title: "Error al iniciar sesión",
        description: error.message || "No se pudo iniciar sesión con Google. Intenta de nuevo.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-background flex flex-col items-center justify-center px-6 py-8 relative"
      style={{
        backgroundImage: `url(${optimizedCoverUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'top',
        backgroundRepeat: 'no-repeat'
      }}
    >


      {/* Content container */}
      <div className="relative z-10 w-full max-w-md">
        <div className="mb-8">
        </div>

        <Card className="bg-background/90 backdrop-blur-sm border-border">
          <CardHeader className="text-center">
            <h1 className="text-3xl font-light text-foreground mb-2">{getSetting("text_home_h1")}</h1>
          </CardHeader>

          <CardContent className="space-y-4">
            <div className="text-sm text-muted-foreground  pb-5">
              <span className="block pb-2 text-sm font-semibold">Al acceder podrás:</span>
              <ul className="space-y-2 text-left">
                <li className="grid grid-cols-[auto_1fr] gap-x-2 items-start">
                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-foreground/60" aria-hidden="true" />
                  <span>Subir tus mejores fotos y administrarlas.</span>
                </li>

                <li className="grid grid-cols-[auto_1fr] gap-x-2 items-start">
                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-foreground/60" aria-hidden="true" />
                  <span>Interactuar con las fotos de los demás.</span>
                </li>

                {getBooleanSetting('enable_guestbook', false) && (
                  <li className="grid grid-cols-[auto_1fr] gap-x-2 items-start">
                    <span className="mt-2 h-1.5 w-1.5 rounded-full bg-foreground/60" aria-hidden="true" />
                    <span>Dejar tu mensaje en el Libro de Visitas.</span>
                  </li>
                )}
              </ul>
            </div>
            <Button
              onClick={handleGoogleSignIn}
              disabled={loading}
              className="w-full h-12 bg-primary hover:bg-secondary text-primary-foreground font-medium rounded-lg transition-colors mb-10"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Iniciando sesión...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  Continuar con Google
                </>
              )}
            </Button>

            <p className="text-xs text-muted-foreground text-center">
              Al continuar, aceptas crear un registro de usuario en la app <span className="italic">Bloomkeep</span>, para el evento: <span className="italic">{albumSlug}</span>.
            </p>

          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Auth;